<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3';
import { Library, Upload } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import ZoteroConnectCard from '@/components/integrations/ZoteroConnectCard.vue';
import ZoteroImportDialog from '@/components/integrations/ZoteroImportDialog.vue';
import ZoteroImportDrawer from '@/components/integrations/ZoteroImportDrawer.vue';
import { useProjectChannel } from '@/composables/useProjectChannel';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import FileUploadZone from '@/pages/Files/FileUploadZone.vue';
import type { Pdf, Project } from '@/types';

interface ZoteroStatus {
    connected: boolean;
    zotero_user_id?: string;
    connected_at?: string;
    last_synced_at?: string;
}

interface Props {
    project: Project;
    files: Pdf[];
    zotero: ZoteroStatus;
}

const props = defineProps<Props>();
const toast = useToast();

defineOptions({
    layout: (props: { project: Project }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: props.project.name,
                    href: `/projects/${props.project.id}`,
                },
                { title: 'Fichiers', href: '#' },
            ],
        },
    ],
});

const importDialogOpen = ref(false);
const importDrawerOpen = ref(false);
const importRunId = ref<string | null>(null);

function openImportDialog() {
    importDialogOpen.value = true;
}

function onImportStarted(runId: string) {
    importRunId.value = runId;
    importDrawerOpen.value = true;
}

function onImportDone() {
    importDrawerOpen.value = false;
    router.visit(`/projects/${props.project.id}/articles`);
}

const { projectEvents } = useProjectChannel(props.project.id);

watch(projectEvents, (events) => {
    const last = events[events.length - 1];

    if (!last) {
        return;
    }

    if (last.type === 'project.file_uploaded') {
        const data = last.payload.data as Record<string, unknown>;
        toast.success(
            `Fichier "${data.original_filename}" traité avec succès.`,
        );
        router.reload({ only: ['files'] });
    }

    if (last.type === 'project.pdf_matched') {
        toast.success('Un PDF a été associé automatiquement à un article.');
        router.reload({ only: ['files'] });
    }
});
</script>

<template>
    <Head :title="`Import · ${project.name}`" />

    <div class="flex-1 overflow-y-auto">
        <div class="mx-auto max-w-4xl px-10 py-12">
            <!-- Page header -->
            <div class="mb-10">
                <p
                    class="mb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                >
                    Préparation
                </p>
                <h1 class="mb-1 text-2xl font-bold tracking-tight">
                    Importer vos articles
                </h1>
                <p class="text-sm text-muted-foreground">
                    Importez vos références depuis Zotero ou déposez directement
                    des fichiers PDF.
                </p>
            </div>

            <div class="space-y-10">
                <!-- Section 1 : Articles depuis Zotero -->
                <section>
                    <div class="mb-5 flex items-start gap-3">
                        <div
                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand"
                        >
                            <Library class="h-4 w-4" />
                        </div>
                        <div>
                            <h2 class="text-sm font-semibold text-foreground">
                                Import d'articles depuis Zotero
                            </h2>
                            <p class="mt-0.5 text-xs text-muted-foreground">
                                Synchronisez vos références bibliographiques —
                                titre, auteurs, DOI, résumé et PDF attachés —
                                directement depuis votre bibliothèque Zotero.
                            </p>
                        </div>
                    </div>
                    <div class="max-w-sm">
                        <ZoteroConnectCard
                            :zotero="zotero"
                            @import="openImportDialog"
                        />
                    </div>
                </section>

                <!-- Séparateur -->
                <div class="flex items-center gap-4">
                    <div class="flex-1 border-t border-border" />
                    <span class="text-xs text-muted-foreground">ou</span>
                    <div class="flex-1 border-t border-border" />
                </div>

                <!-- Section 2 : Dépôt de fichiers PDF -->
                <section data-tour="pdf-upload-section">
                    <div class="mb-5 flex items-start gap-3">
                        <div
                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
                        >
                            <Upload class="h-4 w-4" />
                        </div>
                        <div>
                            <h2 class="text-sm font-semibold text-foreground">
                                Dépôt de fichiers PDF
                            </h2>
                            <p class="mt-0.5 text-xs text-muted-foreground">
                                Importez vos fichiers PDF directement. Ils
                                seront associés automatiquement aux articles
                                existants dans votre projet.
                            </p>
                        </div>
                    </div>
                    <FileUploadZone :upload-url="`/library/files`" />
                    <a
                        href="#"
                        class="mt-3 block text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                        Voir tous les fichiers ({{ files.length }})
                    </a>
                </section>
            </div>
        </div>
    </div>

    <ZoteroImportDialog
        :open="importDialogOpen"
        :project-id="project.id"
        @update:open="importDialogOpen = $event"
        @started="onImportStarted"
    />

    <ZoteroImportDrawer
        :open="importDrawerOpen"
        :run-id="importRunId"
        :project-id="project.id"
        @update:open="importDrawerOpen = $event"
        @done="onImportDone"
    />
</template>
